/* @flow */
import * as React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import moment from 'moment';

import ElectroDatePicker from 'forms/components/inputs/ElectroDatePicker/ElectroDatePicker';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import type { AvailableFilter, AvailableFilterOption } from 'tables/components/ReportView/types';
import { stringToTimeRange, timeRangeToString } from 'stores/ReportViewStore';

export type DateFilterProps = {
  filter: AvailableFilter,
  option: AvailableFilterOption,
  value: string,
  disabled?: boolean,

  onChange?: (newValue: string) => void,
};

type InjectedProps = TranslatorProps;

@translate()
@observer
export default class DateFilter extends ComponentWithInjectedProps<
  DateFilterProps,
  {},
  InjectedProps
> {
  handleDateChange = (field: 'to' | 'from', newDate?: Date, clearDate?: boolean) => {
    const { onChange, value } = this.props;

    if (onChange) {
      const range: { to: string | Date, from: string | Date } = stringToTimeRange(value);
      range[field] = clearDate ? '' : newDate || '';

      onChange(timeRangeToString(range.from, range.to));
    }
  };

  render() {
    const { t, option, value, filter, disabled } = this.props;
    const { to, from } = stringToTimeRange(value);

    const convertedTo = to ? moment(to).toDate() : null;
    const convertedFrom = from ? moment(from).toDate() : null;

    const maxDate = convertedTo || option.to;
    const minDate = convertedFrom || option.from;
    const convertedMaxDate = maxDate ? moment(maxDate).toDate() : null;
    const convertedMinDate = minDate ? moment(minDate).toDate() : null;

    return (
      <React.Fragment>
        <li key={filter.key + '-from'}>
          <ElectroDatePicker
            floatingLabelText={t('general:labels.from_date')}
            maxDate={convertedMaxDate}
            value={convertedFrom}
            onChange={(e, newDate) => this.handleDateChange('from', newDate)}
            clearDate={() => this.handleDateChange('from', undefined, true)}
            disabled={disabled}
          />
        </li>
        <li key={filter.key + '-to'}>
          <ElectroDatePicker
            floatingLabelText={t('general:labels.to_date')}
            minDate={convertedMinDate}
            value={convertedTo}
            onChange={(e, newDate) => this.handleDateChange('to', newDate)}
            clearDate={() => this.handleDateChange('to', undefined, true)}
            disabled={disabled}
          />
        </li>
      </React.Fragment>
    );
  }
}
